import * as Sentry from "@sentry/nextjs";
import { useTranslation } from "next-i18next";
import { OpenSeaAsset } from "opensea-js/lib/types";
import { FC, useCallback, useState } from "react";

import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { useNftContext } from "@/features/nft/hooks/useNft";
import TextPlaceholder from "@/features/placeholder/TextPlaceholder";
import { useMasonryOpenSeaAssets } from "@/features/post/components/utils/InfiniteMasonryReducer";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { useConfirmNftPurchaseNotificationMutation } from "@/generated/generated-hooks";
import {
  PostPagePostFragment,
  UploadWithIntrinsicSizeFragment,
} from "@/server/graphql/generated/generated-types";

import { PutOnSaleButton } from "./PutOnSaleButton";

const NFTActionButton: FC<{
  onBuy?: (setLoadingCallback?: (state?: boolean) => void) => Promise<void>;
  onSale?: (startAmount: number) => Promise<void>;
  asset?: OpenSeaAsset;
  post: PostPagePostFragment & { images?: UploadWithIntrinsicSizeFragment[] };
  fullWidth?: boolean;
  isNFTOwner: boolean;
}> = (props) => {
  // For i18n translation
  const { t } = useTranslation(["common", "post"]);
  const [confirmPurchase] = useConfirmNftPurchaseNotificationMutation();

  const { asset, isNFTOwner, post, fullWidth = false, onBuy, onSale } = props;
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  // get current user
  const { user } = useUserContext();
  // get current users wallet info & nft api
  const {
    activeAccount,
    seaport,
    connectOrOnBoard,
    isSameWallet,
    isSameNetwork,
  } = useNftContext();
  // get [openSeaAssetHashmap, dispatch] from OpenSeaAssetsContext
  const [openSeaAssetHashmap, dispatch, refetchSingleAsset] =
    useMasonryOpenSeaAssets();
  // For error display
  const { openSnackbar } = useSnackbar();

  // function for buying this asset - public
  const buyNFTAsset = useCallback(
    async (asset: OpenSeaAsset) => {
      // if isOwner, construct nothing
      if (isNFTOwner) return;

      // ask user to conenct to wallet if not connected
      if (!activeAccount) {
        return connectOrOnBoard();
      }
      if (!isSameNetwork) {
        return openSnackbar({
          message: t("common:nft.networkMismatch"),
          severity: "error",
        });
      }
      if (!isSameWallet) {
        return openSnackbar({
          message: t("common:nft.pleaseSwitchWallet", {
            walletAccount: user.walletAccount,
          }),
          severity: "error",
        });
      }
      // if yes --> buy the asset
      // disable the buy button
      setButtonDisabled(true);
      try {
        // const startTime = new Date();
        const transactionHash = await seaport.fulfillOrder({
          order: asset.sellOrders[0],
          accountAddress: activeAccount,
        });
        await confirmPurchase({
          variables: {
            transactionHash,
          },
        });
        // success buy --> refetch asset and update the Hashmap'
        setTimeout(async () => {
          //if not under infinitemasory --> no need to execute refetch function
          await refetchSingleAsset(asset.tokenId, post._id);
          setButtonDisabled(false);
        }, 1500);
      } catch (e) {
        if (e.message.includes("insufficient funds")) {
          openSnackbar({
            message: t("common:nft.errors.insufficientFunds"),
            severity: "error",
          });
        }
        if (e.message.includes("Failed to authorize transaction")) {
          openSnackbar({
            message: t("common:nft.errors.userDeclineTransaction"),
            severity: "error",
          });
        }
        openSnackbar({
          message: t("common:nft.errors.transactionFail"),
          severity: "error",
        });
        Sentry.captureException(e, {
          tags: { seaport: "fulfillOrder" },
          extra: { tokenId: asset.tokenId },
        });
        setButtonDisabled(false);
      }
      return;
    },
    [
      isNFTOwner,
      activeAccount,
      isSameWallet,
      connectOrOnBoard,
      openSnackbar,
      t,
      user,
      seaport,
      refetchSingleAsset,
      post,
      confirmPurchase,
      isSameNetwork,
    ],
  );

  // if no asset yet --> return loading skeleton
  if (!asset) {
    <div className={fullWidth ? "w-full" : "min-w-150"}>
      <TextPlaceholder size="lg" />
    </div>;
  }

  const assetListed = asset.sellOrders?.length > 0;

  // if current user is the owner of focal nft
  if (isNFTOwner) {
    // if the asset is listed --> owner can redirect to his/her collectible page
    if (assetListed) {
      return (
        <OutlinedButton
          fullWidth={fullWidth}
          variant="secondary"
          buttonProps={{
            disabled: true,
          }}
        >
          {t("common:nft.myCollectible")}
        </OutlinedButton>
      );
    }
    // if not listed yet, can put on sale
    return (
      <PutOnSaleButton
        loading={buttonDisabled}
        tokenId={asset.tokenId}
        postId={post._id}
        isNFTOwner={isNFTOwner}
        fullWidth={fullWidth}
        onSale={onSale}
        buttonProps={{
          disabled: buttonDisabled || (activeAccount && !isSameWallet),
        }}
      />
    );
  }

  // is current user does not own the focal nft and the nft is not listed --> display nothing
  if (!assetListed) {
    return null;
  }

  // display the buy button if the asset is listed when user dont own the focal nft
  return (
    <div className="min-w-150 max-w-xl">
      <OutlinedButton
        fullWidth={fullWidth}
        loading={buttonDisabled}
        variant="primary"
        buttonProps={{
          disabled: buttonDisabled || (activeAccount && !isSameWallet),
          onClick: () =>
            onBuy ? onBuy(setButtonDisabled) : buyNFTAsset(asset),
        }}
      >
        {t("common:nft.buyButton")}
      </OutlinedButton>
    </div>
  );
};

export default NFTActionButton;
